import {gql} from "@apollo/client";
import {useEffect} from "react";
import {UseFormReturn} from "react-hook-form";
import {ReferralInquiryInput} from "../domain";
import {useLazyQuery$} from "../../../graphqlsupport";

export type LatestReferralInquiryData = {
    // for reusing referral inquiry input
    guid: string;
    referralGuid: string;
} & ReferralInquiryInput;

const REFERRAL_LATEST_REFERRAL_INQUIRY = gql`
query referralInquiryByReferral($referralGuid: UUID) {
    referralInquiryByReferral(referralGuid: $referralGuid) {
        referralGuid
        referralInquiry
    }
}
`;

function useLatestReferralInquiryInputQuery$(referralGuid?: string) {
    const {execute, data, isRunning$, error} = useLazyQuery$<{
        referralGuid?: string
    }, {
        referralInquiryByReferral: LatestReferralInquiryData
    }>(REFERRAL_LATEST_REFERRAL_INQUIRY, { referralGuid });

    return {
        execute,
        data,
        isRunning$,
        error
    };
}

export function useLatestReferralInquiryInput(showModal: boolean, methods: UseFormReturn<ReferralInquiryInput>, referralGuid?: string, readOnly: boolean = false) {
    const {execute, data, isRunning$, error} = useLatestReferralInquiryInputQuery$(referralGuid);
    
    useEffect(() => {
        // Only execute the query if readOnly = true (we need existing data)
        // Skip the query if readOnly = false to avoid unnecessary server calls
        if (showModal && referralGuid != null && readOnly) {
            execute({
                referralGuid,
            });
        }
    }, [referralGuid, showModal, execute, readOnly]);
    
    useEffect(() => {
        if (readOnly) {
            // When readOnly = true, handle the data from server query
            if (data?.referralInquiryByReferral != null) {
                // Populate form with existing data when there's an open submission
                methods.reset({
                    ...methods.getValues(),
                    ...(data.referralInquiryByReferral as any),
                });
            } else if (data && data.referralInquiryByReferral == null && showModal) {
                // When modal is open and there's no existing data, reset to empty form
                methods.reset({
                    referralInquiry: ""
                });
            }
        } else {
            // When readOnly = false, always clear the form text
            if (showModal) {
                methods.reset({
                    referralInquiry: ""
                });
            }
        }
    }, [data, methods, showModal, readOnly]);
    
    return {
        isRunning$,
        error,
    };
}
